import React, { useState } from "react";
import useApi from "@/hooks/useApi";
import CopilotAssistant from "@/components/CopilotAssistant";

interface RetriggerResult {
  outcomeType?: string;
  outcomeLabel?: string;
  ruleName?: string;
  conditionsEvaluated?: number;
  failedRules?: Array<{ ruleName: string; reason: string }>;
  missDetail?: string;
}

interface Props {
  workflowId: string;
  workflowName: string;
  triggerType: string;
  onClose: () => void;
}

export default function WorkflowDiagnosticPanel({ workflowId, workflowName, triggerType, onClose }: Props) {
  const api = useApi();
  const [conversationId, setConversationId] = useState("");
  const [simStatus, setSimStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [simResult, setSimResult] = useState<RetriggerResult | null>(null);
  const [simError, setSimError] = useState<string | null>(null);
  const [showCopilot, setShowCopilot] = useState(false);

  async function simulate() {
    const id = conversationId.trim();
    if (!id) return;
    setSimStatus("loading");
    setSimResult(null);
    setSimError(null);
    try {
      const res = await api.messaging.post(`RoutingHistory/conversations/${id}/retrigger`, {});
      if (res.ok) {
        const json = await res.json().catch(() => ({}));
        setSimResult(json);
        setSimStatus("done");
      } else {
        setSimError(`API returned ${res.status} — retrigger not supported or conversation not found`);
        setSimStatus("error");
      }
    } catch {
      setSimError("Network error — check backend status");
      setSimStatus("error");
    }
  }

  function buildCopilotPrefill(): string {
    const parts: string[] = [`I'm building a workflow named "${workflowName}"`];
    if (triggerType) parts.push(`with trigger type "${triggerType}"`);
    if (conversationId) parts.push(`and testing against conversation #${conversationId}`);
    parts.push(".");

    if (simResult) {
      parts.push(`\n\nSimulation result:`);
      parts.push(`• Outcome: ${simResult.outcomeLabel ?? simResult.outcomeType ?? "unknown"}`);
      if (simResult.ruleName) parts.push(`• Matched rule: ${simResult.ruleName}`);
      if (typeof simResult.conditionsEvaluated === "number") {
        parts.push(`• Conditions evaluated: ${simResult.conditionsEvaluated}`);
      }
      if (simResult.failedRules?.length) {
        parts.push(`• Failed rules: ${simResult.failedRules.map(r => `${r.ruleName} (${r.reason})`).join(", ")}`);
      }
      if (simResult.missDetail) parts.push(`• Miss reason: ${simResult.missDetail}`);
    }

    parts.push("\n\nCan you help me understand why this workflow isn't triggering as expected and how to fix it?");
    return parts.join("\n");
  }

  return (
    <>
      <div className="fixed right-0 top-0 h-full w-[380px] bg-white border-l border-slate-200 shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-violet-400" />
            <span className="text-sm font-semibold text-slate-700">Diagnose Workflow</span>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors text-lg leading-none"
            aria-label="Close diagnostic panel"
          >
            ×
          </button>
        </div>

        {/* Workflow info */}
        <div className="px-4 py-3 bg-primary-bg border-b border-slate-100">
          <p className="text-xs font-medium text-primary-active truncate">{workflowName}</p>
          {triggerType ? (
            <p className="text-xs text-slate-400 mt-0.5">
              Trigger: <span className="text-slate-600">{triggerType}</span>
            </p>
          ) : null}
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-6" style={{ minHeight: 0 }}>

          {/* Simulate routing section */}
          <section>
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
              Simulate Routing
            </h3>
            <p className="text-xs text-slate-500 mb-3">
              Enter a past conversation ID to re-run the routing engine in read-only mode. Nothing changes — no agents notified, no workflows started.
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Conversation ID…"
                value={conversationId}
                onChange={(e) => setConversationId(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && simulate()}
                className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-hover"
              />
              <button
                onClick={simulate}
                disabled={!conversationId.trim() || simStatus === "loading"}
                className="px-3 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
              >
                {simStatus === "loading" ? "…" : "↺ Simulate"}
              </button>
            </div>

            {simStatus === "done" && simResult && (
              <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs space-y-1.5">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-600">Outcome:</span>
                  <span className={`px-2 py-0.5 rounded-full font-medium ${
                    simResult.outcomeType === "no_match"
                      ? "bg-red-100 text-red-700"
                      : "bg-emerald-100 text-emerald-700"
                  }`}>
                    {simResult.outcomeLabel ?? simResult.outcomeType ?? "—"}
                  </span>
                </div>
                {simResult.ruleName && (
                  <div>
                    <span className="font-medium text-slate-600">Rule matched:</span>{" "}
                    {simResult.ruleName}
                  </div>
                )}
                {typeof simResult.conditionsEvaluated === "number" && (
                  <div>
                    <span className="font-medium text-slate-600">Conditions evaluated:</span>{" "}
                    {simResult.conditionsEvaluated}
                  </div>
                )}
                {(simResult.failedRules?.length ?? 0) > 0 && (
                  <details className="mt-1">
                    <summary className="cursor-pointer font-medium text-slate-600">
                      Failed rules ({simResult.failedRules!.length})
                    </summary>
                    <ul className="mt-1 pl-3 space-y-0.5">
                      {simResult.failedRules!.map((r, i) => (
                        <li key={i} className="text-slate-500">
                          <span className="font-medium text-slate-600">{r.ruleName}</span> — {r.reason}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
                {simResult.missDetail && (
                  <div>
                    <span className="font-medium text-red-600">Miss reason:</span>{" "}
                    {simResult.missDetail}
                  </div>
                )}
              </div>
            )}

            {simStatus === "error" && simError && (
              <p className="mt-2 text-xs text-red-500">{simError}</p>
            )}
          </section>

          {/* Ask Moshky */}
          <section>
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
              Ask Moshky
            </h3>
            <p className="text-xs text-slate-500 mb-3">
              Moshky can explain why a workflow isn't triggering, help fix conditions, and suggest improvements.
            </p>
            <button
              onClick={() => setShowCopilot(true)}
              className="w-full flex items-center justify-center gap-2 text-sm text-primary border border-primary-bg rounded-xl py-3 hover:bg-primary-bg transition-colors"
              aria-label="Ask Moshky for workflow help"
            >
              <span className="text-base">✦</span>
              Ask Moshky for help
            </button>
          </section>

          {/* Tips */}
          <section className="text-xs text-slate-400 space-y-1.5 border-t border-slate-100 pt-4">
            <p className="font-medium text-slate-500">Diagnostics tips</p>
            <p>• Routing History shows every routing decision with a full step-by-step trace — click any conversation ID to open it.</p>
            <p>• Trigger types are channel-specific: chat triggers won't fire for IVR calls.</p>
            <p>• Rules evaluate in priority order — the first match wins.</p>
            <p>• Use the Simulate button above to verify a rule fix before the next live conversation arrives.</p>
          </section>
        </div>
      </div>

      {showCopilot && (
        <CopilotAssistant
          context={`workflows/diagnostic — Admin is diagnosing workflow "${workflowName}" (trigger: ${triggerType || "unknown"}). Help them understand why it isn't triggering, how conditions work, and how to fix common issues.`}
          prefillMessage={buildCopilotPrefill()}
          onClose={() => setShowCopilot(false)}
        />
      )}
    </>
  );
}
